import isEmpty from 'lodash/isEmpty';
import { useQuery } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getPosSyncedOffers } from 'shared/helpers/specials';
import { FilteredSpecialsDocument } from 'src/gql/graphql';
import type { Specials, Products } from 'src/gql/graphql';

export type ProductOffersInput = Pick<Products, 'DispensaryID' | 'POSPricingData' | 'specialData'>;

const MENU_PRICING_ROLLOUT_FLAG = 'core.cats-ecomm.dpos-menu-pricing.rollout';
const POS_SYNCED_PRICING_VARIANTS = [2, 3];

export default function useProductOffers(product: ProductOffersInput): { specials: Specials[]; loading: boolean } {
  const flags = useFlags();
  const isPosSyncedPricingActive = POS_SYNCED_PRICING_VARIANTS.includes(Number(flags[MENU_PRICING_ROLLOUT_FLAG]));
  const legacyOffers = product.specialData?.bogoSpecials ?? [];
  const qualifyingLegacyOffers = legacyOffers.filter(
    (special) => special?.qualifyingOptions && special.qualifyingOptions.length > 0
  );
  // Two distinct questions, deliberately kept on separate booleans:
  //
  // `hasLegacyOffers` -- does this product have legacy offers it actually QUALIFIES for? This
  // drives the FilteredSpecials fetch, so a special attached with empty qualifyingOptions (e.g. a
  // 7g product against an eighths-only BOGO) is neither fetched nor rendered.
  //
  // `hasAttachedLegacyOffers` -- does this product have ANY legacy special attached, qualifying or
  // not? This alone decides whether the POS-synced fallback below applies. Keying that fallback on
  // the qualifying set instead would mean tightening legacy eligibility silently reroutes a product
  // to a different offer source: a product whose only legacy specials are non-qualifying would stop
  // showing those specials (correct) but start showing POSPricingData stand-ins (a separate change,
  // under a separate flag). Legacy attachment keeps ownership of the product's offer display.
  const hasLegacyOffers = !isEmpty(qualifyingLegacyOffers);
  const hasAttachedLegacyOffers = !isEmpty(legacyOffers);

  const { data, loading } = useQuery(FilteredSpecialsDocument, {
    skip: !hasLegacyOffers,
    variables: {
      specialsFilter: {
        dispensaryId: product.DispensaryID,
        current: true,
        specialIds: qualifyingLegacyOffers.map((s) => s?.specialId).filter((id): id is string => id != null),
      },
    },
  });

  if (!hasAttachedLegacyOffers && isPosSyncedPricingActive) {
    // No legacy bogoSpecials to fetch via FilteredSpecials -- if this product has POS-synced
    // condition/reward discounts instead, build lightweight Specials-shaped stand-ins directly
    // from POSPricingData rather than a second round-trip. There's no Specials Mongo document
    // backing these ids, but the offer card components only ever read _id/menuDisplayName/name/
    // specialType off a special, so this is sufficient to render and link them.
    const posSyncedOffers = getPosSyncedOffers(product).map(
      ({ id, menuDisplayName }) =>
        (({
          _id: id,
          specialId: id,
          menuDisplayName,
          name: menuDisplayName,
          specialType: 'bogo',
        } as unknown) as Specials)
    );

    return { specials: posSyncedOffers, loading: false };
  }

  const specials = (data?.filteredSpecials?.specials ?? []).filter((s): s is Specials => s != null);

  return { specials, loading };
}
